import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowUpRight, Clock, Star } from 'lucide-react';
import { getCategoryBySlug, serviceCategories } from '../data/servicesData';
import BookingModal from '../components/BookingModal';

const ServiceCategory = () => {
  const { categorySlug } = useParams();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  const category = getCategoryBySlug(categorySlug);

  if (!category) {
    return (
      <main className="bg-black min-h-screen pt-32">
        <Helmet>
          <title>Category Not Found | BLR Nail Club</title>
        </Helmet>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl text-white mb-4">Category Not Found</h1>
          <Link to="/services" className="text-[#B8860B] hover:underline">
            ← Back to Services
          </Link>
        </div>
      </main>
    );
  }

  const shareUrl = `https://www.blrnailclub.in/services/${categorySlug}`;
  
  // Count total services in category
  const totalServices = category.subCategories.reduce((sum, sub) => sum + sub.services.length, 0);
  
  // Get price range
  const allPrices = category.subCategories.flatMap(sub => 
    sub.services.map(s => parseInt(s.price.replace(/[^0-9]/g, '')))
  );
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);

  return (
    <>
      <Helmet>
        <title>{`${category.name} Services in Bangalore | Best ${category.name} Salon - BLR Nail Club`}</title>
        <meta name="description" content={`${category.description}. ${totalServices}+ ${category.name.toLowerCase()} treatments starting from Rs. ${minPrice}. Expert stylists at RR Nagar, Basaveshwarnagar & Varthur. Book now!`} />
        <meta name="keywords" content={`${category.name.toLowerCase()} services Bangalore, best ${category.name.toLowerCase()} salon RR Nagar, ${category.name.toLowerCase()} near me, ${category.name.toLowerCase()} Basaveshwarnagar, affordable ${category.name.toLowerCase()} treatments`} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={`${category.name} Services | BLR Nail Club Bangalore`} />
        <meta property="og:description" content={`${totalServices}+ ${category.name.toLowerCase()} treatments. Expert stylists. Book now!`} />
        <meta property="og:image" content={category.image} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `${category.name} Services`,
            "description": category.description,
            "provider": {
              "@type": "BeautySalon",
              "name": "BLR Nail Club",
              "address": [
                {
                  "@type": "PostalAddress",
                  "streetAddress": "N2VK Arcade, 1154, Jawaharlal Nehru Rd",
                  "addressLocality": "Rajarajeshwari Nagar",
                  "addressRegion": "Bangalore",
                  "postalCode": "560098"
                }
              ],
              "telephone": "+91-7204602211"
            },
            "areaServed": ["RR Nagar", "Basaveshwarnagar", "Varthur"],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `${category.name} Services`,
              "itemListElement": category.subCategories.map((sub, idx) => ({
                "@type": "OfferCatalog",
                "name": sub.name,
                "description": sub.description,
                "numberOfItems": sub.services.length
              }))
            },
            "priceRange": `Rs. ${minPrice} - Rs. ${maxPrice}`
          })}
        </script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid="service-category-page">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={`${category.name} Services in Bangalore`}
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
            {/* Breadcrumb */}
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="text-white/50 hover:text-[#B8860B] transition-colors">Home</Link>
                </li>
                <li className="text-white/30">/</li>
                <li>
                  <Link to="/services" className="text-white/50 hover:text-[#B8860B] transition-colors">Services</Link>
                </li>
                <li className="text-white/30">/</li>
                <li className="text-[#B8860B]">{category.name}</li>
              </ol>
            </nav>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
              {category.name} Services
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl font-light mb-8">
              {category.description}
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-white/10">
              <div>
                <span className="text-[#B8860B] text-4xl font-light">{category.subCategories.length}</span>
                <p className="text-white/50 text-sm mt-1">Service Categories</p>
              </div>
              <div>
                <span className="text-[#B8860B] text-4xl font-light">{totalServices}+</span>
                <p className="text-white/50 text-sm mt-1">Treatments</p>
              </div>
              <div>
                <span className="text-[#B8860B] text-4xl font-light">Rs. {minPrice}</span>
                <p className="text-white/50 text-sm mt-1">Starting From</p>
              </div>
              <div>
                <span className="text-[#B8860B] text-4xl font-light">3</span>
                <p className="text-white/50 text-sm mt-1">Locations</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Book CTA */}
        <section className="py-6 bg-[#B8860B]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-black font-medium text-lg">Ready to book your {category.name.toLowerCase()} appointment?</p>
              <p className="text-black/70 text-sm">Expert stylists • Premium products • 3 convenient locations</p>
            </div>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="bg-black text-white px-8 py-3 text-sm tracking-wider hover:bg-black/80 transition-colors whitespace-nowrap"
            >
              Book Now
            </button>
          </div>
        </section>

        {/* Sub-Categories Grid */}
        <section className="py-16 md:py-24 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-light text-white">
                  Explore {category.name} Services
                </h2>
                <p className="text-white/50 mt-2">
                  Choose from {category.subCategories.length} specialized {category.name.toLowerCase()} categories
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {category.subCategories.map((subCat) => (
                <Link
                  key={subCat.id}
                  to={`/services/${categorySlug}/${subCat.slug}`}
                  className="group bg-black border border-white/10 overflow-hidden hover:border-[#B8860B]/50 transition-all duration-500 hover:transform hover:-translate-y-1"
                  data-testid={`subcategory-${subCat.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={subCat.image}
                      alt={`${subCat.name} in Bangalore`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
                    {subCat.trending && (
                      <span className="absolute top-4 left-4 bg-[#B8860B] text-black px-3 py-1 text-xs tracking-wider font-medium flex items-center gap-1">
                        <Star size={12} fill="currentColor" />
                        Trending
                      </span>
                    )}
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-[#B8860B]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="text-white text-xl font-light tracking-wide group-hover:text-[#B8860B] transition-colors">
                        {subCat.name}
                      </h3>
                      <ArrowUpRight 
                        className="text-[#B8860B] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 mt-1" 
                        size={20} 
                      />
                    </div>
                    <p className="text-white/60 text-sm font-light leading-relaxed mb-5 line-clamp-2">
                      {subCat.description}
                    </p>
                    
                    {/* Services Preview */}
                    <div className="mb-5 space-y-2">
                      {subCat.services.slice(0, 3).map((service, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-white/50 truncate pr-2">{service.name}</span>
                          <span className="text-[#B8860B] whitespace-nowrap">{service.price}</span>
                        </div>
                      ))}
                      {subCat.services.length > 3 && (
                        <p className="text-white/30 text-xs">+{subCat.services.length - 3} more treatments</p>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="pt-5 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-4 text-white/40 text-xs">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {subCat.services[0]?.duration}
                        </span>
                        <span>{subCat.services.length} treatments</span>
                      </div>
                      <span className="text-[#B8860B] text-sm tracking-wider group-hover:translate-x-1 transition-transform">
                        Explore →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-16 bg-black border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-light text-white mb-6">
              Best {category.name} Services in Bangalore
            </h2>
            <div className="prose prose-invert prose-sm max-w-none text-white/60 font-light leading-relaxed space-y-4">
              <p>
                Looking for professional {category.name.toLowerCase()} services in Bangalore? BLR Nail Club offers 
                premium {category.name.toLowerCase()} treatments at our conveniently located salons in RR Nagar, 
                Basaveshwarnagar, and CSK Mall Varthur. Our expert stylists use high-quality products to deliver 
                exceptional results.
              </p>
              <p>
                We offer {totalServices}+ specialized {category.name.toLowerCase()} treatments including{' '}
                {category.subCategories.slice(0, 3).map(s => s.name.toLowerCase()).join(', ')}, and more. 
                Prices start from just Rs. {minPrice}, making premium {category.name.toLowerCase()} services 
                accessible to everyone.
              </p>
              <p>
                Book your appointment today and experience the difference at BLR Nail Club – Bangalore's 
                trusted destination for {category.name.toLowerCase()} services.
              </p>
            </div>
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-16 bg-[#0a0a0a] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-light text-white mb-8">Explore Other Services</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {serviceCategories
                .filter(cat => cat.slug !== categorySlug)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/services/${cat.slug}`}
                    className="group relative h-36 overflow-hidden rounded-lg"
                  >
                    <img
                      src={cat.image}
                      alt={`${cat.name} Services`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors"></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                      <span className="text-white text-lg font-light tracking-wider group-hover:text-[#B8860B] transition-colors">
                        {cat.name}
                      </span>
                      <span className="text-white/40 text-xs mt-1">
                        {cat.subCategories.length} categories
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-black border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready for Your {category.name} Transformation?
            </h2>
            <p className="text-white/60 font-light mb-8">
              Visit us at RR Nagar, Basaveshwarnagar, or CSK Mall Varthur. Walk-ins welcome!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-[#B8860B] text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#9A7B0A] transition-colors"
                data-testid="category-book-btn"
              >
                Book Appointment
              </button>
              <a
                href="tel:+917204602211"
                className="border border-white/20 text-white px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
              >
                Call: +91 72046 02211
              </a>
            </div>
          </div>
        </section>

        {/* Booking Modal */}
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </main>
    </>
  );
};

export default ServiceCategory;
